import React, { useState, useMemo, useCallback } from "react";
import CompleteMessage from "../CompleteMessage";
import Citations from "./Citations";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlinkingLoader from "../../../general/BlinkingLoader";
import PlotlyChart from "../../../general/PlotlyChart";
import { applyCustomClasses } from "../../../../utils/applyCustomClasses";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import { IconButton, Tooltip } from "@mui/material";
import "./ServerMessage.css";

const ServerMessage = ({ content, suggestionKey, citations, isStreaming, onRegenerate }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'up' | 'down' | null

  const closeLightbox = useCallback(() => setLightboxImage(null), []);

  const isGraphData = useMemo(() => {
    try {
      const parsedData = JSON.parse(content);
      return parsedData && parsedData.data && parsedData.layout;
    } catch (error) {
      return false;
    }
  }, [content]);

  const messageContent = useMemo(() => {
    if (isGraphData) {
      return <PlotlyChart graphDataString={content} />;
    } else {
      const modifiedContent = applyCustomClasses(content);
      return <div dangerouslySetInnerHTML={{ __html: modifiedContent }} />;
    }
  }, [content, isGraphData]);

  const handleCopy = useCallback(() => {
    // Strip HTML tags to get plain text
    const temp = document.createElement("div");
    temp.innerHTML = content;
    const plainText = temp.textContent || temp.innerText || "";
    navigator.clipboard.writeText(plainText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [content]);

  const handleFeedback = useCallback((type) => {
    setFeedback((prev) => (prev === type ? null : type));
  }, []);

  return (
    <>
      <CompleteMessage
        type="server"
        icon={
          <div>
            <AutoAwesomeIcon className="ai-icon" style={{ marginTop: "4px" }} />
          </div>
        }
        message={
          <div className="server-message">
            {suggestionKey && <h4 className="server-message-header">{suggestionKey}:</h4>}
            <div className="server-message-body">
              {messageContent}
            </div>
            {isStreaming && <BlinkingLoader />}
            <Citations citations={citations} />

            {/* Action bar — only show when not streaming and has content */}
            {!isStreaming && content && (
              <div className="message-actions">
                <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    className={`action-btn ${copied ? "action-btn-active" : ""}`}
                  >
                    {copied ? (
                      <CheckIcon style={{ fontSize: 16 }} />
                    ) : (
                      <ContentCopyIcon style={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Good response" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleFeedback("up")}
                    className={`action-btn ${feedback === "up" ? "action-btn-active" : ""}`}
                  >
                    {feedback === "up" ? (
                      <ThumbUpAltIcon style={{ fontSize: 16 }} />
                    ) : (
                      <ThumbUpOffAltIcon style={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Bad response" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleFeedback("down")}
                    className={`action-btn ${feedback === "down" ? "action-btn-active" : ""}`}
                  >
                    {feedback === "down" ? (
                      <ThumbDownAltIcon style={{ fontSize: 16 }} />
                    ) : (
                      <ThumbDownOffAltIcon style={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>

                {onRegenerate && (
                  <Tooltip title="Regenerate" arrow>
                    <IconButton
                      size="small"
                      onClick={onRegenerate}
                      className="action-btn"
                    >
                      <ReplayIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        }
      />
      {lightboxImage && (
        <div className="lightbox open" onClick={closeLightbox}>
          <span className="close" onClick={closeLightbox}>&times;</span>
          <img src={lightboxImage} className="lightbox-content" alt="Enlarged content" />
        </div>
      )}
    </>
  );
};

export default ServerMessage;
